import { useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { PERMISSIONS } from '@/config/permissions'
import type { PermissionName } from '@/config/permissions'

/** Routes ordered by priority for determining landing page. */
const PERMISSION_ROUTES: { permission: PermissionName; path: string }[] = [
  { permission: PERMISSIONS.USERS_VIEW, path: '/users' },
  { permission: PERMISSIONS.SUPPORT_VIEW, path: '/support' },
  { permission: PERMISSIONS.NOTIFICATIONS_SEND, path: '/announcements' },
  { permission: PERMISSIONS.REPORTS_VIEW, path: '/reports' },
  { permission: PERMISSIONS.SETTINGS_VIEW, path: '/settings' },
]

export function usePermissions() {
  const { user } = useAuth()

  // Role strings are canonical lowercase (matches backend utils/roles.ts).
  // Lowercase defensively so legacy rows with 'ADMIN'/'SUPER_ADMIN' still resolve.
  const normalizedRole = user?.role?.toLowerCase() ?? ''
  const isSuperAdmin = normalizedRole === 'super_admin'
  const isBuiltInAdmin = normalizedRole === 'super_admin' || normalizedRole === 'admin'
  const permissions = user?.permissions ?? []

  const helpers = useMemo(() => {
    // Mirrors backend hasPermission(): `<resource>.manage` is an umbrella that
    // grants the CRUD-style actions on the same resource. Needed because the
    // seeded Permission table uses `*.manage` names while route guards check
    // for `*.create` / `.update` / `.delete`.
    const manageGrantedActions = new Set(['view', 'create', 'update', 'delete', 'edit'])

    const holds = (permission: string): boolean => {
      if (permissions.includes(permission)) return true
      const dot = permission.indexOf('.')
      if (dot > 0) {
        const resource = permission.slice(0, dot)
        const action = permission.slice(dot + 1)
        if (manageGrantedActions.has(action) && permissions.includes(`${resource}.manage`)) {
          return true
        }
      }
      return false
    }

    const hasPermission = (permission: PermissionName): boolean => {
      if (isSuperAdmin) return true
      if (permissions.includes('*')) return true
      return holds(permission)
    }

    const hasAnyPermission = (perms: PermissionName[]): boolean => {
      if (isSuperAdmin) return true
      if (permissions.includes('*')) return true
      return perms.some(holds)
    }

    const hasAllPermissions = (perms: PermissionName[]): boolean => {
      if (isSuperAdmin) return true
      if (permissions.includes('*')) return true
      return perms.every(holds)
    }

    /** Returns the best landing page for the current user. */
    const getLandingPage = (): string => {
      // SUPER_ADMIN and ADMIN always land on dashboard
      if (isBuiltInAdmin) return '/dashboard'
      // Custom roles: find first permitted route
      for (const route of PERMISSION_ROUTES) {
        if (hasPermission(route.permission)) return route.path
      }
      // Fallback: profile page (always accessible)
      return '/profile'
    }

    return { hasPermission, hasAnyPermission, hasAllPermissions, getLandingPage }
  }, [isSuperAdmin, isBuiltInAdmin, permissions])

  return { ...helpers, isSuperAdmin, isBuiltInAdmin, permissions }
}
